import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, AlertCircle, ToggleLeft, ToggleRight, Star, Link as LinkIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MAX_MESSAGE = 350;

const SpotlightPage = () => {
  const [step, setStep] = useState('loading');
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [message, setMessage] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const r = await fetch(`${API}/api/spotlights/packages`);
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (!data.enabled) { setStep('unavailable'); return; }
        if (!data.configured || !data.packages?.length) { setStep('not_configured'); return; }
        setPackages(data.packages);
        setSelectedPkg(data.packages[0]?.key || null);
      } catch { /* assume available */ }

      const params = new URLSearchParams(window.location.search);
      if (params.get('checkout') === 'complete') {
        window.history.replaceState({}, '', window.location.pathname);
        const pendingId = sessionStorage.getItem('med_pending_spotlight_id');
        const orderId = sessionStorage.getItem('med_pending_spotlight_order');
        if (pendingId && orderId) {
          setStep('confirming');
          startPolling(pendingId, orderId);
          return;
        }
      }
      setStep('form');
    };
    init();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = (pendingId, orderId) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/api/spotlights/pending/${pendingId}/status?order_id=${orderId}`);
        const data = await res.json();
        if (data.status && data.status !== 'pending_payment') {
          clearInterval(pollRef.current);
          sessionStorage.removeItem('med_pending_spotlight_id');
          sessionStorage.removeItem('med_pending_spotlight_order');
          if (data.status === 'expired') {
            setError('Request expired. Please try again.');
            setStep('error');
          } else {
            setStep('submitted');
          }
          return;
        }
      } catch { /* retry */ }
      if (attempts >= 60) {
        clearInterval(pollRef.current);
        setError('Payment verification timed out. If you were charged, your spotlight will be processed automatically.');
        setStep('error');
      }
    }, 2000);
  };

  const selectedPackage = packages.find(p => p.key === selectedPkg);
  const priceDisplay = selectedPackage?.price_display || '$3';

  const handlePayAndSubmit = async () => {
    const msg = message.trim();
    if (!msg) { setError('Spotlight message is required.'); return; }
    if (msg.length > MAX_MESSAGE) { setError(`Message too long (max ${MAX_MESSAGE} characters).`); return; }
    if (!selectedPkg) { setError('Please select a spotlight length.'); return; }
    if (linkUrl.trim() && !linkUrl.trim().match(/^https?:\/\/.+\..+/)) {
      setError('Invalid URL. Must start with http:// or https://.'); return;
    }
    setError('');
    setStep('processing');
    try {
      const pendingRes = await fetch(`${API}/api/spotlights/pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: displayName.trim() || null,
          is_anonymous: isAnonymous,
          message: msg,
          link_url: linkUrl.trim() || null,
          package: selectedPkg,
        }),
      });
      if (!pendingRes.ok) {
        const err = await pendingRes.json();
        throw new Error(err.detail || 'Failed to create spotlight');
      }
      const { pending_id, variation_id } = await pendingRes.json();

      const checkoutRes = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variation_id,
          quantity: 1,
          entitlement_type: 'spotlight',
          redirect_path: '/spotlight',
          pending_submission_id: pending_id,
        }),
      });
      if (!checkoutRes.ok) throw new Error('Failed to create checkout');
      const { url, order_id } = await checkoutRes.json();

      sessionStorage.setItem('med_pending_spotlight_id', pending_id);
      sessionStorage.setItem('med_pending_spotlight_order', order_id);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={28} className="text-[#E91E8D] animate-spin" />
      </div>
    );
  }

  if (step === 'unavailable') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="spotlight-unavailable">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
          <div className="glass-card p-10">
            <Star size={40} className="text-white/10 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Spotlights Unavailable</h1>
            <p className="text-white/40 text-sm">Spotlights are not available at this time. Please check back later.</p>
            <a href="/" className="inline-block mt-6 text-[#E91E8D] text-sm font-medium" data-testid="back-home-link">&larr; Back to Mediterranean</a>
          </div>
        </motion.div>
      </div>
    );
  }

  if (step === 'not_configured') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="glass-card p-10 text-center max-w-md">
          <Star size={40} className="text-white/10 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Coming Soon</h1>
          <p className="text-white/40 text-sm">Spotlights are being set up. Check back shortly.</p>
          <a href="/" className="inline-block mt-6 text-[#E91E8D] text-sm font-medium">&larr; Back to Mediterranean</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="spotlight-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/30 mb-4">
            <Star size={28} className="text-[#E91E8D]" />
          </div>
          <h1 className="text-2xl font-bold text-white" data-testid="spotlight-title">Spotlight the Room</h1>
          <p className="text-white/40 text-sm mt-1">Put your message in front of the room.</p>
        </div>

        {step === 'form' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-6 space-y-4" data-testid="spotlight-form">
            {/* Display Name */}
            <div>
              <label className="block text-[#E91E8D]/80 text-sm mb-1">Display Name <span className="text-white/20">(optional)</span></label>
              <input
                type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                placeholder="Your name" maxLength={100}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-[#E91E8D]/30 transition-colors"
                data-testid="input-display-name"
              />
            </div>

            {/* Anonymous Toggle */}
            <div className="flex items-center justify-between py-1">
              <span className="text-white/50 text-sm">Post Anonymously</span>
              <button type="button" onClick={() => setIsAnonymous(!isAnonymous)} className="flex items-center gap-1.5 text-sm" data-testid="toggle-anonymous">
                {isAnonymous
                  ? <ToggleRight size={28} className="text-[#E91E8D]" />
                  : <ToggleLeft size={28} className="text-white/30" />}
                <span className={isAnonymous ? 'font-medium text-[#E91E8D]' : 'text-white/30'}>
                  {isAnonymous ? 'Yes' : 'No'}
                </span>
              </button>
            </div>

            {/* Message */}
            <div>
              <label className="block text-[#E91E8D]/80 text-sm mb-1">Spotlight Message *</label>
              <textarea
                value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Your shout-out, announcement, promo, or message..."
                rows={4} maxLength={MAX_MESSAGE}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-[#E91E8D]/30 transition-colors resize-none"
                data-testid="input-message"
              />
              <div className="flex justify-end mt-1">
                <span className={`text-[10px] ${message.length > MAX_MESSAGE - 30 ? 'text-[#E91E8D]' : 'text-white/20'}`} data-testid="char-counter">
                  {message.length}/{MAX_MESSAGE}
                </span>
              </div>
            </div>

            {/* Link URL */}
            <div>
              <label className="block text-[#E91E8D]/80 text-sm mb-1">
                <span className="flex items-center gap-1.5">
                  <LinkIcon size={12} /> Website / Social Link <span className="text-white/20">(optional)</span>
                </span>
              </label>
              <input
                type="url" value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
                placeholder="https://instagram.com/yourhandle"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-[#E91E8D]/30 transition-colors"
                data-testid="input-link-url"
              />
            </div>

            {/* Package Selection */}
            <div>
              <label className="block text-[#E91E8D]/80 text-sm mb-2">Select Spotlight Length</label>
              <div className="space-y-2" data-testid="package-selector">
                {packages.map(pkg => (
                  <button
                    key={pkg.key}
                    type="button"
                    onClick={() => setSelectedPkg(pkg.key)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 rounded-lg border transition-all ${
                      selectedPkg === pkg.key
                        ? 'border-[#E91E8D]/50 bg-[#E91E8D]/10'
                        : 'border-white/10 bg-black/20 hover:border-white/20'
                    }`}
                    data-testid={`package-${pkg.key}`}
                  >
                    <span className={selectedPkg === pkg.key ? 'text-white font-semibold text-sm' : 'text-white/50 text-sm'}>
                      {pkg.label}
                    </span>
                    <span className={`font-bold text-sm ${selectedPkg === pkg.key ? 'text-[#E91E8D]' : 'text-white/30'}`}>
                      {pkg.price_display}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="spotlight-error">
                <AlertCircle className="w-4 h-4 shrink-0" /> <span>{error}</span>
              </div>
            )}

            <button
              onClick={handlePayAndSubmit}
              disabled={!message.trim() || !selectedPkg}
              className="w-full bg-gradient-to-r from-[#E91E8D] to-[#6366F1] text-white font-semibold rounded-lg px-6 py-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_0_30px_rgba(233,30,141,0.3)]"
              data-testid="pay-and-submit-btn"
            >
              Pay {priceDisplay} & Start Spotlight
            </button>
            <p className="text-white/20 text-xs text-center">Secure payment via Square. Spotlight goes live after payment.</p>
          </motion.div>
        )}

        {step === 'processing' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-8 text-center" data-testid="spotlight-processing">
            <Loader2 className="w-10 h-10 text-[#E91E8D] animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Preparing your spotlight...</p>
            <p className="text-white/40 text-sm mt-1">Redirecting to payment</p>
          </motion.div>
        )}

        {step === 'confirming' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-8 text-center" data-testid="spotlight-confirming">
            <Loader2 className="w-10 h-10 text-[#E91E8D] animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Verifying payment...</p>
            <p className="text-white/40 text-sm mt-1">Your spotlight will go live automatically</p>
          </motion.div>
        )}

        {step === 'submitted' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-[#E91E8D]/20 to-[#6366F1]/20 backdrop-blur-lg rounded-xl border border-[#E91E8D]/30 p-8 text-center"
            data-testid="spotlight-submitted"
          >
            <CheckCircle className="w-12 h-12 text-[#E91E8D] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Spotlight is Live!</h2>
            <p className="text-white/60 text-sm">Your spotlight is now showing in the room ticker.</p>
            <p className="text-white/40 text-xs mt-3">Payment verified successfully.</p>
            <a href="/" className="inline-block mt-6 text-[#E91E8D] text-sm font-medium" data-testid="back-home-link">
              &larr; Back to Mediterranean Club
            </a>
          </motion.div>
        )}

        {step === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-500/10 backdrop-blur-lg rounded-xl border border-red-400/30 p-8 text-center" data-testid="spotlight-error-screen">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-white/60 text-sm">{error}</p>
            <button onClick={() => { setStep('form'); setError(''); }} className="mt-4 text-[#E91E8D] text-sm font-medium">Try again</button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default SpotlightPage;
